import { readonly, ref } from 'vue';
import axios from '@/plugins/axios.js';

/**
 * Composable for managing recent global searches
 * Uses database storage via API for privacy and persistence
 */
// eslint-disable-next-line import/prefer-default-export
export function useRecentSearches() {
    const recentSearches = ref([]);
    const isLoading = ref(false);

    /**
     * Load recent searches from API
     * @returns {Promise<Array>} Array of recent search objects
     */
    const loadSearches = async () => {
        try {
            isLoading.value = true;
            const { data } = await axios.get('/api/recent-searches');
            recentSearches.value = data.data || [];
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('Failed to load recent searches:', error);
            }
            recentSearches.value = [];
        } finally {
            isLoading.value = false;
        }
        return recentSearches.value;
    };

    /**
     * Save a search to recent history
     * @param {string} query - The search query to save
     * @param {number} resultCount - Optional count of results returned
     */
    const saveSearch = async (query, resultCount = null) => {
        if (!query || typeof query !== 'string' || query.trim().length < 2) {
            return;
        }

        try {
            await axios.post('/api/recent-searches', {
                query: query.trim(),
                result_count: resultCount,
            });
            // Optimistically update the local state
            await loadSearches();
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('Failed to save recent search:', error);
            }
        }
    };

    /**
     * Remove a search by ID
     * @param {number} id - ID of the search to remove
     */
    const removeSearch = async id => {
        try {
            await axios.delete(`/api/recent-searches/${id}`);
            // Optimistically remove from local state
            recentSearches.value = recentSearches.value.filter(search => search.id !== id);
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('Failed to remove recent search:', error);
            }
        }
    };

    /**
     * Clear all recent searches
     */
    const clearAll = async () => {
        try {
            await axios.delete('/api/recent-searches');
            recentSearches.value = [];
        } catch (error) {
            if (process.env.NODE_ENV !== 'production') {
                console.error('Failed to clear recent searches:', error);
            }
        }
    };

    return {
        recentSearches: readonly(recentSearches),
        isLoading: readonly(isLoading),
        loadSearches,
        saveSearch,
        removeSearch,
        clearAll,
    };
}
